import * as React from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { Transition } from 'react-transition-group';

import CloseButton from './CloseButton';
import Backdrop from './Backdrop';
import { KEYCODES } from '../../consts';

const MODAL_ANIMATION_TIMEOUT_ON_CLOSE = 0;
const MODAL_ANIMATION_TIMEOUT_ON_OPEN = 300;

type TransitionState = 'entering' | 'entered' | 'exiting' | 'exited';

type ModalProps = {
    isOpen: boolean;
    isFullPage?: boolean;
    isFixed?: boolean;
    rootId?: string;
    onClose?: () => void;
    closeColor?: string;
    closeIsOutside?: boolean;
    hideCloseButton?: boolean;
    overrideWidth?: number;
    zIndex?: number;
    disableBackgroundScroll?: boolean;
    className?: string;
};

export const ModalWrapper = styled.div<{ zIndex: number }>`
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: ${props => props.zIndex};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const Container = styled.div<{ isOpen: boolean; isFullPage: boolean; overrideWidth?: number; isFixed?: boolean }>`
    transition: transform 0.2s, opacity 0.2s;
    transform: translateY(${({ isOpen }) => (isOpen ? '0' : '15%')});
    opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
    position: ${({ isFixed }) => (isFixed ? 'fixed' : 'absolute')};
    z-index: 2;
    overflow-y: auto;
    ${p =>
        p.isFullPage
            ? `
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  `
            : `
    top: 50%;
    left: 50%;
    max-height: 100%;
    transform: translate(-50%, -50%);
            `}
    ${p => (p.overrideWidth ? `width: ${p.overrideWidth}px` : '')}
`;

const NoScrollBody = createGlobalStyle`
    body {
        overflow: hidden;
    }
`;

const Modal: React.FunctionComponent<ModalProps> = ({
    isOpen,
    children,
    onClose,
    closeColor,
    rootId = 'modal-root',
    isFullPage = false,
    isFixed = false,
    closeIsOutside = false,
    hideCloseButton = false,
    overrideWidth,
    zIndex = 999,
    disableBackgroundScroll = false,
    className,
}) => {
    // make sure the modal is initialized only after the first full DOM render
    const [isInitialized, setInit] = React.useState(document.getElementById(rootId) !== null);

    React.useEffect(() => {
        const closeOnEscape = (e: KeyboardEvent) => {
            if (e.keyCode === KEYCODES.ESCAPE && onClose) {
                onClose();
            }
        };
        window.addEventListener('keyup', closeOnEscape);
        return () => window.removeEventListener('keyup', closeOnEscape);
    }, [onClose]);

    // this check is required to see if the modal is set to open before the first render
    // (needed to be able the use a portal, which is directly attached to a rendered DOM node)
    React.useLayoutEffect(() => {
        if (!isInitialized && document.getElementById(rootId) !== null) {
            setInit(true);
        }

        if (document.getElementById(rootId) === null) {
            throw new Error(
                `Cannot find node root with id of "${rootId}". Failed initializing modal, make sure your DOM contains the element with the required id.`,
            );
        }
    }, []);

    if (!isInitialized) {
        return null;
    }

    return createPortal(
        <Transition in={isOpen} timeout={isOpen ? MODAL_ANIMATION_TIMEOUT_ON_CLOSE : MODAL_ANIMATION_TIMEOUT_ON_OPEN}>
            {(state: TransitionState) =>
                ['entering', 'entered', 'exiting'].includes(state) && (
                    <ModalWrapper zIndex={zIndex}>
                        <Backdrop
                            isOpen={['entered'].includes(state)}
                            onClick={() => onClose && onClose()}
                            isFixed={isFixed}
                        />
                        <Container
                            isOpen={['entered'].includes(state)}
                            isFullPage={isFullPage}
                            isFixed={isFixed}
                            overrideWidth={overrideWidth}
                            className={className}
                        >
                            {onClose && !hideCloseButton && (
                                <CloseButton onClose={onClose} color={closeColor} isOutside={closeIsOutside} />
                            )}
                            {children}
                        </Container>
                        {disableBackgroundScroll && <NoScrollBody />}
                    </ModalWrapper>
                )
            }
        </Transition>,
        document.getElementById(rootId) as HTMLElement,
    );
};

export default Modal;
